import {
  DropdownOption,
  GroupMembership,
  Pot,
  UpdateGroupMembership,
} from '@aslanpay/portal-common-lib';
import { GroupWithPot } from 'app/hooks/useGroupsWithPot/types';
import { getGroupDisplayNameWithParents } from 'app/utils/groupsWithPot/groupsWithPot';
import { CreatePotWithPotManagersAndBalanceParams } from 'app/hooks/usePots/useCreatePotWithPotManagersAndBalance';
import { getPointsFromValue } from 'app/utils/points';

import { CreatePotFormFields } from './CreatePotForm.types';

function formatCreatePotRequestFromForm({
  companyPot,
  data,
  groups = [],
  members = [],
}: {
  companyPot?: Pot;
  data: CreatePotFormFields;
  groups?: Omit<GroupWithPot, 'children'>[];
  members?: GroupMembership[];
}): CreatePotWithPotManagersAndBalanceParams {
  const { amount, potManagers, group } = data;

  const memberMap = new Map(members.map((m) => [m.individualId, m]));

  const changeMembers: UpdateGroupMembership[] = members
    .map((member) => {
      const isSelected = potManagers?.includes(member.individualId);

      if (isSelected) {
        return {
          individualId: member.individualId,
          membershipRole: 'POT_MANAGER',
          isPrimaryGroup: member.isPrimaryGroup,
        };
      }

      if (member.membershipRole === 'POT_MANAGER' && !isSelected) {
        // Was a POT_MANAGER, now unselected: demote to MEMBER
        return {
          individualId: member.individualId,
          membershipRole: 'MEMBER',
          isPrimaryGroup: member.isPrimaryGroup,
        };
      }

      return null;
    })
    .filter(Boolean) as UpdateGroupMembership[];

  const addMembers: UpdateGroupMembership[] = (potManagers ?? [])
    .filter((id) => !memberMap.has(id))
    .map((id) => ({ individualId: id, membershipRole: 'POT_MANAGER', isPrimaryGroup: false }));

  return {
    pointsToTransfer: amount ? getPointsFromValue(parseFloat(amount)) || 0 : 0,
    changeMembers,
    addMembers,
    companyPot,
    group: groups.find((g) => g.id === group),
  };
}

function formatOptionsForDropdown(groups: GroupWithPot[]): DropdownOption[] {
  const options: DropdownOption[] = groups.map((group) => ({
    text: getGroupDisplayNameWithParents(group),
    name: group.name,
    value: group.id,
    hasPot: !!group.pot,
    isDisabled: !!group.pot,
  }));

  return options;
}

export { formatCreatePotRequestFromForm, formatOptionsForDropdown };
